import { useState, useCallback, useRef } from "react";
import {
  getEnrolmentPresignedUrl,
  bulkUploadDynamic,
  bulkUploadFixed,
  bulkUploadIncomplete,
  ENROLMENT_TEMPLATE_URLS,
  type EnrolmentBulkUploadResult,
  type IncompleteBulkUploadEnrolledThrough,
} from "../../../api/academyEnrolments/enrolmentsService";
import { getImageUrl } from "../../../utils/imageUrl";

export type UploadType = "dynamic" | "fixed" | "incomplete";
type UploadStep = "type-select" | "incomplete-source-select" | "upload" | "result";
export type { IncompleteBulkUploadEnrolledThrough };

interface UseBulkUploadModalParams {
  show: boolean;
  onHide: () => void;
  onSuccess?: () => void;
}

export function useBulkUploadModal({
  show: _show,
  onHide,
  onSuccess,
}: UseBulkUploadModalParams) {
  const [step, setStep] = useState<UploadStep>("type-select");
  const [uploadType, setUploadType] = useState<UploadType | null>(null);
  const [incompleteEnrolledThrough, setIncompleteEnrolledThrough] =
    useState<IncompleteBulkUploadEnrolledThrough>("Offline");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadResult, setUploadResult] = useState<EnrolmentBulkUploadResult | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSelectType = useCallback((type: UploadType) => {
    setUploadType(type);
    setStep(type === "incomplete" ? "incomplete-source-select" : "upload");
    setIncompleteEnrolledThrough("Offline");
    setSelectedFile(null);
    setUploadError(null);
    setUploadProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, []);

  const handleSelectIncompleteEnrolledThrough = useCallback(
    (enrolledThrough: IncompleteBulkUploadEnrolledThrough) => {
      setIncompleteEnrolledThrough(enrolledThrough);
      setStep("upload");
      setSelectedFile(null);
      setUploadError(null);
      setUploadProgress(0);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
    []
  );

  const handleBackToTypeSelect = useCallback(() => {
    setStep("type-select");
    setUploadType(null);
    setIncompleteEnrolledThrough("Offline");
    setSelectedFile(null);
    setUploadError(null);
    setUploadProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, []);

  const handleBackFromUpload = useCallback(() => {
    if (uploadType === "incomplete") {
      setStep("incomplete-source-select");
    } else {
      setStep("type-select");
      setUploadType(null);
    }
    setSelectedFile(null);
    setUploadError(null);
    setUploadProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, [uploadType]);

  const handleFileChange = useCallback((file: File | null) => {
    setSelectedFile(file);
    setUploadError(null);
  }, []);

  const handleUpload = useCallback(async () => {
    if (!selectedFile) {
      setUploadError("Please select a file to upload");
      return;
    }

    if (!uploadType) {
      setUploadError("Please select an upload type");
      return;
    }

    const fileName = selectedFile.name.toLowerCase();
    if (!fileName.endsWith(".xlsx") && !fileName.endsWith(".xls")) {
      setUploadError("Only .xlsx or .xls files are supported");
      return;
    }

    setIsUploading(true);
    setUploadError(null);
    setUploadProgress(0);

    try {
      // Step 1: Get presigned URL
      setUploadProgress(25);
      const presignedResponse = await getEnrolmentPresignedUrl({
        fileName: selectedFile.name,
        contentType: selectedFile.type,
        context: "spreadsheets",
      });

      const presignedUrl = presignedResponse.presignedUrl;
      const fileKey = presignedResponse.key || presignedResponse.fileKey;

      if (!presignedUrl || !fileKey) {
        throw new Error("Failed to get presigned URL from server");
      }

      // Step 2: PUT file directly to S3 (no auth header — use native fetch)
      setUploadProgress(50);
      const s3Response = await fetch(presignedUrl, {
        method: "PUT",
        body: selectedFile,
        headers: {
          "Content-Type": selectedFile.type,
        },
      });

      if (!s3Response.ok) {
        const errorText = await s3Response.text();
        throw new Error(`Failed to upload file to storage: ${s3Response.status} - ${errorText}`);
      }

      // Step 3: Send fileKey to the type-specific endpoint
      setUploadProgress(75);
      let response: any;
      if (uploadType === "dynamic") {
        response = await bulkUploadDynamic(fileKey);
      } else if (uploadType === "fixed") {
        response = await bulkUploadFixed(fileKey);
      } else {
        response = await bulkUploadIncomplete(fileKey, incompleteEnrolledThrough);
      }

      setUploadProgress(100);

      // Normalise the response into EnrolmentBulkUploadResult shape
      const result: EnrolmentBulkUploadResult = normaliseUploadResponse(response);
      setUploadResult(result);
      setStep("result");

      if (!result.isValidationError && (result.successCount > 0 || result.skippedCount > 0)) {
        onSuccess?.();
      }
    } catch (err: unknown) {
      let message = "Failed to upload file. Please try again.";
      if (err && typeof err === "object") {
        const anyErr = err as any;
        const respData = anyErr.response?.data;
        if (respData && typeof respData === "object") {
          message =
            (respData.error as string | undefined) ||
            (respData.message as string | undefined) ||
            message;
        } else if ("message" in anyErr && typeof anyErr.message === "string") {
          message = anyErr.message;
        }
      } else if (err instanceof Error) {
        message = err.message;
      }
      setUploadError(message);
      setUploadProgress(0);
    } finally {
      setIsUploading(false);
    }
  }, [selectedFile, uploadType, incompleteEnrolledThrough, onSuccess]);

  const handleDownloadTemplate = useCallback(() => {
    if (!uploadType) return;
    const path = ENROLMENT_TEMPLATE_URLS[uploadType];
    if (!path) return;

    // Resolve the CDN URL via the same utility used for all CDN assets in this project
    const fullUrl = getImageUrl(path);
    if (!fullUrl) return;

    const a = document.createElement("a");
    a.href = fullUrl;
    a.download = path.split("/").pop() || "template.xlsx";
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }, [uploadType]);

  const handleReset = useCallback(() => {
    setStep("upload");
    setSelectedFile(null);
    setUploadResult(null);
    setUploadError(null);
    setUploadProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, []);

  const handleClose = useCallback(() => {
    setStep("type-select");
    setUploadType(null);
    setIncompleteEnrolledThrough("Offline");
    setSelectedFile(null);
    setUploadResult(null);
    setUploadError(null);
    setUploadProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    onHide();
  }, [onHide]);

  return {
    functions: {
      handleSelectType,
      handleSelectIncompleteEnrolledThrough,
      handleBackToTypeSelect,
      handleBackFromUpload,
      handleFileChange,
      handleUpload,
      handleDownloadTemplate,
      handleReset,
      handleClose,
    },
    values: {
      step,
      uploadType,
      incompleteEnrolledThrough,
      selectedFile,
      uploadResult,
      uploadError,
      uploadProgress,
      fileInputRef,
    },
    loading: {
      isUploading,
    },
  };
}

function normaliseUploadResponse(response: any): EnrolmentBulkUploadResult {
  const outer = response ?? {};

  // Determine whether the payload is wrapped (fixed/incomplete) or flat (dynamic):
  //   Wrapped: { data: { success, data: [...], errors: [], message }, message }
  //   Flat:    { data: [...], message }
  const isWrapped = outer.data && !Array.isArray(outer.data) && typeof outer.data === "object";
  const inner = isWrapped ? outer.data : null;

  // Validation error shape (both flat and wrapped use this via inner):
  // { data: { success: false, errors: string[], message, data: [] }, message, ... }
  if (inner && inner.success === false && Array.isArray(inner.errors)) {
    return {
      records: [],
      successCount: 0,
      skippedCount: 0,
      failedCount: 0,
      message: outer.message ?? "",
      isValidationError: true,
      validationErrors: inner.errors,
      validationMessage: inner.message ?? "Validation failed. Please fix the highlighted errors.",
    };
  }

  // Resolve the records array from whichever shape is present:
  //   Wrapped success: response.data.data  (fixed / incomplete)
  //   Flat success:    response.data        (dynamic)
  const rawRecords: any[] = Array.isArray(inner?.data)
    ? inner.data
    : Array.isArray(outer.data)
    ? outer.data
    : [];

  const records: EnrolmentBulkUploadResult["records"] = rawRecords.map((item: any) => ({
    player: item.player ?? item.name ?? "Unknown",
    status: item.status ?? "Failed",
    reason: item.reason,
  }));

  const successCount = records.filter((r) => r.status === "Success").length;
  const skippedCount = records.filter((r) => r.status === "Skipped").length;
  const failedCount = records.filter((r) => r.status === "Failed").length;

  // Prefer the inner message (more specific) over the outer one
  const message = inner?.message ?? outer.message ?? "";

  return {
    records,
    successCount,
    skippedCount,
    failedCount,
    message,
    isValidationError: false,
    validationErrors: [],
    validationMessage: "",
  };
}
